import React, { useEffect, useState, useRef, useContext, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import performRequest from "../../../network/perform-request";
import { competitionsCalendarEventsRoute, competitionsShowRoute } from "../../../network/api-routes";
import { Box, CircularProgress, Grid, Dialog, Chip, FormControlLabel, Switch } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FullCalendar from "@fullcalendar/react";
import resourceTimeGrid from "@fullcalendar/resource-timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import scrollGridPlugin from "@fullcalendar/scrollgrid";
import EditMatchModal from "../Tournament/EditMatchModal";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import RevaText from "components/common/RevaText";
import UserContext from "context/UserContext";
import { formatMatchIdentity } from "components/common/matchIdentity";
import { getCalendarSurfaceStyles, getSchedulingEventColors, getTournamentEventColors } from "./calendarEventColors";
import { DEFAULT_DAY_SLOT_RANGE, getDateKey, getSlotRangeForDate } from "./calendarTimeRange";
import {
  buildCompetitionCalendarEvents,
  filterCompetitionCalendarEvents,
  getEventTournamentId,
  getTournamentId,
} from "./competitionCalendarEvents";

const getMonthRange = (date) => {
  const monthStart = dayjs(date).startOf("month");

  return {
    cacheKey: monthStart.format("YYYY-MM"),
    start: monthStart.format(),
    end: monthStart.add(1, "month").format(),
  };
};

const CompetitionCalendar = ({ competitionID }) => {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const theme = useTheme();
  const { user } = useContext(UserContext);
  const tournamentColors = getTournamentEventColors(theme);
  const schedulingColors = getSchedulingEventColors(theme);

  const calendarRef = useRef(null);
  const calendarContainerRef = useRef(null);
  const eventsCacheRef = useRef(new Map());
  const latestRequestRef = useRef(0);
  const currentRangeKeyRef = useRef(null);
  const [competition, setCompetition] = useState(null);
  const [resources, setResources] = useState([]);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [activeTournamentId, setActiveTournamentId] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentViewType, setCurrentViewType] = useState("dayGridMonth");
  const [currentDateKey, setCurrentDateKey] = useState(null);
  const [showClubOccupancy, setShowClubOccupancy] = useState(false);
  const [showFullDay, setShowFullDay] = useState(false);
  const [daySlotRange, setDaySlotRange] = useState(DEFAULT_DAY_SLOT_RANGE);
  const visibleCalendarEvents = useMemo(
    () => filterCompetitionCalendarEvents(calendarEvents, currentViewType, currentDateKey, showClubOccupancy),
    [calendarEvents, currentDateKey, currentViewType, showClubOccupancy],
  );

  useEffect(() => {
    performRequest(
      "POST",
      competitionsShowRoute,
      { competition_uuid: competitionID, include_calendar_resources: true },
      true,
      user,
    )
      .then((response) => {
        setCompetition(response.data.data);
        setResources(response.data.meta.calendar_resources);
        eventsCacheRef.current.clear();
        currentRangeKeyRef.current = null;
        setCalendarEvents([]);
        setReady(true);
      })
      .catch(console.log);
  }, [competitionID, user]);

  const loadEventsForDate = useCallback(
    async (date, { force = false } = {}) => {
      if (!competition) {
        return;
      }

      const range = getMonthRange(date);

      currentRangeKeyRef.current = range.cacheKey;

      if (!force && eventsCacheRef.current.has(range.cacheKey)) {
        setCalendarEvents(eventsCacheRef.current.get(range.cacheKey));
        return;
      }

      const requestId = latestRequestRef.current + 1;

      latestRequestRef.current = requestId;
      setLoading(true);

      try {
        const response = await performRequest(
          "POST",
          competitionsCalendarEventsRoute,
          {
            competition_id: competition.id,
            start: range.start,
            end: range.end,
          },
          true,
          user,
        );

        if (latestRequestRef.current !== requestId) {
          return;
        }

        eventsCacheRef.current.set(range.cacheKey, response.data);
        setCalendarEvents(response.data);
      } catch (error) {
        if (latestRequestRef.current === requestId) {
          console.log(error);
          enqueueSnackbar(intl.formatMessage({ id: "scheduling.calendar.refresh_failed" }), { variant: "warning" });
        }
      } finally {
        if (latestRequestRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    [competition, user, enqueueSnackbar, intl],
  );

  const refreshCalendar = () => {
    eventsCacheRef.current.clear();
    performRequest(
      "POST",
      competitionsShowRoute,
      { competition_uuid: competitionID, include_calendar_resources: true },
      true,
      user,
    )
      .then((response) => setResources(response.data.meta.calendar_resources))
      .catch(() =>
        enqueueSnackbar(intl.formatMessage({ id: "scheduling.calendar.refresh_failed" }), { variant: "warning" }),
      );
    loadEventsForDate(calendarRef.current?.getApi().getDate() ?? new Date(), {
      force: true,
    });
  };

  const handleMatchUpdated = () => {
    enqueueSnackbar(intl.formatMessage({ id: "Partido actualizado" }), {
      variant: "success",
    });
    setSelectedMatch(null);
    refreshCalendar();
  };

  const applyEventHighlighting = useCallback(() => {
    const calendarElement = calendarContainerRef.current;

    if (!calendarElement) {
      return;
    }

    const eventElements = calendarElement.querySelectorAll(".fc-event");

    eventElements.forEach((eventElement) => {
      if (eventElement.dataset.eventKind === "booking") {
        eventElement.style.opacity = "";
        eventElement.style.filter = "none";
        eventElement.style.boxShadow = "none";
        eventElement.style.zIndex = "1";
        return;
      }

      const isActiveTournamentEvent = activeTournamentId && eventElement.dataset.tournamentId === activeTournamentId;

      eventElement.style.transition = "opacity 180ms ease, filter 180ms ease, box-shadow 180ms ease";

      if (!activeTournamentId) {
        eventElement.style.opacity = "1";
        eventElement.style.filter = "none";
        eventElement.style.boxShadow = "none";
        eventElement.style.zIndex = "auto";
        return;
      }

      eventElement.style.opacity = isActiveTournamentEvent ? "1" : "0.28";
      eventElement.style.filter = isActiveTournamentEvent ? "none" : "grayscale(0.35) brightness(0.55)";
      eventElement.style.boxShadow = isActiveTournamentEvent ? "0 0 0 2px rgba(255, 255, 255, 0.65)" : "none";
      eventElement.style.zIndex = isActiveTournamentEvent ? "2" : "1";
    });
  }, [activeTournamentId]);

  useEffect(() => {
    if (!loading) {
      applyEventHighlighting();
    }
  }, [applyEventHighlighting, loading]);

  useEffect(() => {
    if (showFullDay || currentViewType !== "resourceTimeGridDay" || !currentDateKey) {
      setDaySlotRange(DEFAULT_DAY_SLOT_RANGE);
      return;
    }

    setDaySlotRange(getSlotRangeForDate(visibleCalendarEvents, currentDateKey));
  }, [currentDateKey, currentViewType, showFullDay, visibleCalendarEvents]);

  const renderLegends = () => {
    if (!competition) return null;
    const tournaments = competition.tournaments.data;
    const hasBookings = visibleCalendarEvents.some((event) => event.event_kind === "booking");
    return (
      <div style={{ marginTop: 8 }}>
        {tournaments.map((tournament, index) => {
          const tournamentId = getTournamentId(tournament);
          const isActive = activeTournamentId === tournamentId;
          const isInactive = Boolean(activeTournamentId) && !isActive;

          return (
            <Chip
              key={tournamentId ?? index}
              size="small"
              label={tournament.name}
              clickable
              onClick={() =>
                setActiveTournamentId((currentTournamentId) =>
                  currentTournamentId === tournamentId ? null : tournamentId,
                )
              }
              style={{
                backgroundColor: tournamentColors[index % tournamentColors.length],
                margin: 4,
                color: theme.palette.common.white,
                cursor: "pointer",
                opacity: isInactive ? 0.5 : 1,
                boxShadow: isActive ? "0 0 0 2px rgba(255, 255, 255, 0.95)" : "none",
              }}
            />
          );
        })}
        {hasBookings && (
          <Chip
            className="scheduling-occupancy scheduling-booking"
            size="small"
            label={intl.formatMessage({ id: "scheduling.calendar.booking" })}
            style={{
              backgroundColor: schedulingColors.booking.backgroundColor,
              margin: 4,
              color: schedulingColors.booking.textColor,
            }}
          />
        )}
      </div>
    );
  };

  if (!ready) {
    return (
      <Grid item container justifyContent="center" style={{ marginTop: 30 }}>
        <CircularProgress size={24} />
      </Grid>
    );
  }

  if (resources.length === 0) {
    return (
      <Grid item container justifyContent="center" style={{ marginTop: 30, textAlign: "center" }}>
        <RevaText id="Aún no hay partidos programados. Asigna una fecha y un lugar a los partidos de tus torneos para verlos aquí." />
      </Grid>
    );
  }

  return (
    <>
      <Box sx={getCalendarSurfaceStyles(theme)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            columnGap: 2,
            mb: 1,
          }}
        >
          <FormControlLabel
            control={
              <Switch checked={showClubOccupancy} onChange={(event) => setShowClubOccupancy(event.target.checked)} />
            }
            label={intl.formatMessage({ id: "scheduling.calendar.showClubOccupancy" })}
          />
          <FormControlLabel
            sx={{ m: 0, whiteSpace: "nowrap" }}
            control={<Switch checked={showFullDay} onChange={(event) => setShowFullDay(event.target.checked)} />}
            label={intl.formatMessage({ id: "Ver día completo" })}
          />
        </Box>
        <div ref={calendarContainerRef} style={{ height: "85vh", position: "relative" }}>
          <FullCalendar
            ref={calendarRef}
            schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
            plugins={[resourceTimeGrid, dayGridPlugin, scrollGridPlugin]}
            initialView="dayGridMonth"
            resources={resources}
            slotMinTime={daySlotRange.minTime}
            slotMaxTime={daySlotRange.maxTime}
            slotLabelFormat={{
              hour: "numeric",
              minute: "2-digit",
              omitZeroMinute: false,
            }}
            resourceOrder="order"
            allDaySlot={false}
            locale={intl.locale}
            headerToolbar={{
              left: "title",
              center: "",
              right: "resourceTimeGridDay,dayGridMonth today prev,next",
            }}
            events={buildCompetitionCalendarEvents({
              events: visibleCalendarEvents,
              tournaments: competition.tournaments.data,
              tournamentColors,
              bookingColors: schedulingColors.booking,
              matchTextColor: theme.palette.common.white,
            })}
            eventDurationEditable={false}
            buttonText={{
              today: intl.formatMessage({ id: "Hoy" }),
              month: intl.formatMessage({ id: "Mes" }),
              day: intl.formatMessage({ id: "Día" }),
            }}
            height="100%"
            navLinks
            expandRows
            slotDuration="00:60:00"
            dayMinWidth={200}
            dayMaxEventRows
            datesSet={(info) => {
              const visibleDate = info.view.type === "resourceTimeGridDay" ? info.start : info.view.currentStart;

              setCurrentViewType(info.view.type);
              setCurrentDateKey(getDateKey(visibleDate));
              loadEventsForDate(visibleDate);
            }}
            eventDidMount={(info) => {
              info.el.title = info.event.title;
              info.el.dataset.eventKind = info.event.extendedProps.event_kind || "match";
              const tournamentId = getEventTournamentId(info.event);

              if (tournamentId) {
                info.el.dataset.tournamentId = tournamentId;
              } else {
                delete info.el.dataset.tournamentId;
              }

              applyEventHighlighting();
            }}
            eventClick={(info) => {
              if (info.event.extendedProps.match) setSelectedMatch(info.event.extendedProps.match);
            }}
            eventContent={(info) => {
              const match = info.event.extendedProps.match?.data;
              if (!match) return <span>{info.event.title}</span>;
              const identity = formatMatchIdentity(match, (id, values) => intl.formatMessage({ id }, values));
              const player1 = match.player1?.data?.name;
              const player2 = match.player2?.data?.name;
              return (
                <div style={{ whiteSpace: "normal", overflowWrap: "anywhere" }} title={info.event.title}>
                  <strong>
                    {info.event.extendedProps.scheduling_pinned && (
                      <span
                        role="img"
                        aria-label={intl.formatMessage({
                          id: "scheduling.calendar.pinned",
                        })}
                      >
                        📌{" "}
                      </span>
                    )}
                    {info.timeText} {identity}
                  </strong>
                  {(player1 || player2) && (
                    <div>
                      {player1 || "—"} <strong>VS</strong> {player2 || "—"}
                    </div>
                  )}
                </div>
              );
            }}
            loading={(isLoading) => setLoading(isLoading)}
          />
          {loading && (
            <div
              style={{
                position: "absolute",
                right: "53px",
                top: "186px",
                zIndex: 10000,
              }}
            >
              <CircularProgress size={48} />
            </div>
          )}
        </div>
        {renderLegends()}
      </Box>
      <Dialog
        open={Boolean(selectedMatch)}
        onBackdropClick={() => setSelectedMatch(null)}
        disableAutoFocus
        fullWidth={false}
        maxWidth="md"
      >
        {selectedMatch && (
          <EditMatchModal
            matchId={selectedMatch.data.id}
            tabName="details"
            isBracketStage={selectedMatch.data.tournament.data.state === "underway"}
            showScoreTab
            onSubmitScore={handleMatchUpdated}
            canEdit
          />
        )}
      </Dialog>
    </>
  );
};

export default CompetitionCalendar;
